import noop from 'lodash/noop';
import { useTranslation } from 'next-i18next';
import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { OrlSortField, TravelType } from '@hotelplan/graphql.types';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import { CustomSelectInput } from 'components/domain/custom-select-input';
import type { ISortingOption } from 'components/domain/orl/Orl.types';

export const ORL_SORTING_LABEL_KEYS: { [key in OrlSortField]: string } = {
  [OrlSortField.Price]: 'orl:sort.price',
  [OrlSortField.DepartureAirport]: 'orl:sort.departureAirport',
  [OrlSortField.DepartureDate]: 'orl:sort.departureDate',
  [OrlSortField.Duration]: 'orl:sort.duration',
};

interface IORLSortingState {
  sortingValue: OrlSortField;
  setSortingValue: (nextSortingValue: OrlSortField) => void;
}

const ORLSortingContext = React.createContext<IORLSortingState>({
  sortingValue: OrlSortField.Price,
  setSortingValue: noop,
});

export const useORLSortingContext = (): IORLSortingState => {
  return useContext(ORLSortingContext);
};

export const ORLSortingContextProvider: React.FC = ({ children }) => {
  const [sortingValue, setSortingValue] = useState(OrlSortField.Price);
  return (
    <ORLSortingContext.Provider
      value={{
        sortingValue,
        setSortingValue,
      }}
    >
      {children}
    </ORLSortingContext.Provider>
  );
};

interface IORLSortingDropdownProps {
  travelType: TravelType;
}

const SortingSelectInput = styled(CustomSelectInput).attrs({
  className: 'sorting-select-input',
})(
  sx2CssThemeFn({
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: ['150px', '180px'],
    '.label': {
      mb: [null, 1],
    },
    '.input': {
      lineHeight: 'normal',
      fontSize: ['12px', 0],
      fontWeight: 'bold',
    },
    '.absolute-dropdown': {
      border: '1px solid #ccc',
      borderTop: 'none',
      boxShadow: '1px 0 2px rgba(203,200,194,0.25)',
    },
  })
);

export const ORLSortingDropdown: React.FC<IORLSortingDropdownProps> = ({
  travelType,
}: IORLSortingDropdownProps) => {
  const { t } = useTranslation(['orl', 'results']);
  const { mobile } = useDeviceType();
  const { setSortingValue, sortingValue } = useORLSortingContext();

  const options = useMemo(() => {
    return [
      {
        label: t(ORL_SORTING_LABEL_KEYS.PRICE),
        value: OrlSortField.Price,
      },
      travelType === TravelType.Package
        ? {
            label: t(ORL_SORTING_LABEL_KEYS.DEPARTURE_AIRPORT),
            value: OrlSortField.DepartureAirport,
          }
        : null,
      {
        label: t(ORL_SORTING_LABEL_KEYS.DEPARTURE_DATE),
        value: OrlSortField.DepartureDate,
      },
      {
        label: t(ORL_SORTING_LABEL_KEYS.DURATION),
        value: OrlSortField.Duration,
      },
    ].filter(Boolean) as ISortingOption[];
  }, [t, travelType]);

  return (
    <SortingSelectInput
      options={options}
      onChange={(nextSortOption: OrlSortField) => {
        const option = options.find(({ value }) => value === nextSortOption);
        if (option) {
          setSortingValue(option.value);
        }
      }}
      value={sortingValue}
      label={t('results:sort.label')}
      hideLabel={mobile}
    />
  );
};
